import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devLog } from '../services/devLog'
import {
  getDocuments,
  getDocument,
  uploadDocument,
  addSigners,
  sendInvitations,
  recallDocument,
  downloadDocument,
  updateDocumentMetadata,
} from '../services/documentsApi'
import type { DocumentListResponse, DocumentDetail } from '../services/documentsApi'
import type { SignaturePlacement } from './useSignaturePlacement'

/**
 * Fetch recent documents with adaptive polling.
 *
 * - 10s when there are pending/draft documents (active signing)
 * - 60s when all documents are completed/recalled/expired (idle)
 */
export interface DocumentFilters {
  folder_id?: number | null
  tags?: number[]
  document_type_id?: number | null
  department_id?: number | null
  status?: string | null
}

export function useDocumentList(params?: {
  page?: number
  enabled?: boolean
  owner?: string
  filters?: DocumentFilters
}) {
  return useQuery<DocumentListResponse>({
    queryKey: ['documents', params?.page, params?.owner, params?.filters],
    queryFn: () => getDocuments({
      page: params?.page,
      owner: params?.owner,
      ...params?.filters,
    }),
    enabled: params?.enabled ?? true,
    placeholderData: (prev) => prev,
    // No polling — real-time updates via SSE (useDocumentListSSE)
    staleTime: 30_000,
  })
}

/**
 * Upload a document from Word and send for signing.
 *
 * 3-step flow:
 * 1. Upload DOCX → creates draft document
 * 2. Add signers to the draft
 * 3. Send invitations (starts signing process)
 */
export function useSendDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      file: Blob
      filename: string
      title: string
      signers: Array<{ name: string; email: string }>
      signing_mode: 'parallel' | 'sequential'
      actorId?: string
      readPlacements?: () => Promise<SignaturePlacement[]>
    }) => {
      devLog.info('Send flow start', `title="${params.title}", signers=${params.signers.length}, actor=${params.actorId || 'default'}, file=${params.file.size} bytes`)

      // Step 1: Upload DOCX (with owner_id for org documents)
      devLog.info('Step 1/3', 'Uploading DOCX...')
      const doc = await uploadDocument(params.file, params.filename, params.title, params.actorId)
      devLog.info('Step 1/3 OK', `koblink_id=${doc.koblink_id}`)

      // Read signature placements from document (if available)
      let placements: SignaturePlacement[] = []
      if (params.readPlacements) {
        try {
          placements = await params.readPlacements()
          devLog.info('Placements', `Found ${placements.length} signature tag(s)`)
        } catch (e) {
          devLog.warn('Placements', `Failed to read placements: ${e}`)
        }
      }

      // Step 2: Add signers with signature field positions
      devLog.info('Step 2/3', `Adding ${params.signers.length} signers...`)
      await addSigners(doc.koblink_id, {
        signing_mode: params.signing_mode,
        document_level: 'SES',
        signers: params.signers.map((signer, index) => {
          const placement = placements.find((p) => p.email === signer.email)
          return {
            name: signer.name,
            email: signer.email,
            step: params.signing_mode === 'sequential' ? index + 1 : undefined,
            field: placement && placement.page > 0
              ? { page: placement.page, x: placement.x, y: placement.y, width: placement.width, height: placement.height }
              : { page: 1, x: 10, y: 75 + index * 8, width: 30, height: 5 },
          }
        }),
      }, params.actorId)
      devLog.info('Step 2/3 OK', 'Signers added')

      // Step 3: Send invitations
      devLog.info('Step 3/3', 'Sending invitations...')
      const result = await sendInvitations(doc.koblink_id, undefined, params.actorId)
      devLog.info('Step 3/3 OK', `${result.invitations_sent} invitations sent`)

      return { document: doc, invitationsSent: result.invitations_sent }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

/** Fetch a single document with full details (signature requests, etc.) */
export function useDocumentDetail(koblinkId: string | undefined, actorId?: string) {
  return useQuery<DocumentDetail>({
    queryKey: ['document', koblinkId, actorId],
    queryFn: () => getDocument(koblinkId!, actorId),
    enabled: !!koblinkId,
    staleTime: 30_000,
    // No polling — real-time updates via SSE (useDocumentListSSE)
  })
}

/** Download a signed document */
export function useDownloadDocument() {
  return useMutation({
    mutationFn: async ({ koblinkId, title }: { koblinkId: string; title: string }) => {
      const blob = await downloadDocument(koblinkId)
      // Trigger browser download
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${title}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}

/** Recall a document */
export function useRecallDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ documentId, actorId }: { documentId: string; actorId?: string }) =>
      recallDocument(documentId, actorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

/** Update document metadata (folder, type, department, etc.) */
export function useUpdateMetadata() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ koblinkId, data, actorId }: { koblinkId: string; data: Record<string, unknown>; actorId?: string }) =>
      updateDocumentMetadata(koblinkId, data, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document', variables.koblinkId] })
    },
  })
}
